/* eslint-disable no-param-reassign */
import { createSlice } from "@reduxjs/toolkit";
import { endOfDay, startOfDay, subDays } from "date-fns";
import {
  FileStructure,
  ICcyConversionOrdersQuery,
  IOnBoarding,
  IOnboardingDraft,
  IUser,
} from "interfaces";
import { IDTypes, KYBStatus } from "interfaces/enums";
import { IRouterSubAccount, IUserEdit, KYBQuery } from "interfaces/router";
import { toIsoString } from "utils";

type SliceState = {
  ccyConversionOrdersQuery: ICcyConversionOrdersQuery;
  selectedOrderId: string;
  selectedDestinationId: string;
  selectedDestinationIndex: number;
  isClaimTransferActionEnabled: boolean;
  showTimes: boolean;
  onboarding: IOnBoarding;
  step: number;
  uploading: Array<string>;
  previewFile: string;
  userEdited?: IUserEdit;
  internalUserEdited?: IUser;
  geolocation: {
    latitude: null | string;
    longitude: null | string;
  };
  kybQuery: KYBQuery;
  subAccount?: IRouterSubAccount;
};

// Setting the default state of live_only allows the system to save in cache the
// initial request to avoid having to make an additional request
const initialState: SliceState = {
  kybQuery: {
    endpoint: "/broker",
    sort_by: "",
    sort_dir: "asc",
  },
  ccyConversionOrdersQuery: {
    live_only: false,
    sub_account_id: "",
    user_id: "",
    order_id: "",
    start_date: toIsoString(startOfDay(subDays(new Date(), 6))),
    end_date: toIsoString(endOfDay(new Date())),
  },
  selectedOrderId: "",
  selectedDestinationId: "",
  selectedDestinationIndex: -1,
  isClaimTransferActionEnabled: false,
  onboarding: {
    id: "",
    status: KYBStatus.KYB_SENT,
    company_name: "",
    email: "",
    regulated: null,
    legal_rep_name: "",
    company_category: "",
    corporate_shareholders: [],
    files: [],
    id_type: IDTypes.INE,
    time_created: new Date().getTime(),
    extracted_data_map: {},
    rfi_dict: {},
  },
  showTimes: false,
  step: 0,
  uploading: [],
  previewFile: "",
  userEdited: undefined,
  internalUserEdited: undefined,
  geolocation: {
    latitude: null,
    longitude: null,
  },
};

export const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    reset: () => initialState,
    setSubaccount: (state, action: { payload: IRouterSubAccount }) => {
      state.subAccount = action.payload;
    },
    resetSubaccount: (state) => {
      state.subAccount = undefined;
    },
    setGeoLocation: (
      state,
      action: { payload: { longitude: string; latitude: string } },
    ) => {
      state.geolocation.latitude = action.payload.latitude;
      state.geolocation.longitude = action.payload.longitude;
    },
    resetLocation: (state) => {
      state.geolocation.latitude = null;
      state.geolocation.longitude = null;
    },
    setInternalUserEdited: (state, action: { payload: IUser }) => {
      state.internalUserEdited = action.payload;
    },
    setUserEdited: (state, action: { payload: any }) => {
      state.userEdited = { ...(state.userEdited || {}), ...action.payload };
    },
    removeUserEdited: (state) => {
      state.userEdited = undefined;
    },
    removeInternalUserEdited: (state) => {
      state.internalUserEdited = undefined;
    },
    toggleShowTimes: (state) => {
      state.showTimes = !state.showTimes;
    },
    forwardStep: (state) => {
      state.step = state.step + 1;
    },
    setUploading: (state, action: { payload: { category: string } }) => {
      state.uploading.push(action.payload.category);
    },
    removeUploading: (state, action: { payload: { category: string } }) => {
      state.uploading = state.uploading.filter(
        (s) => s !== action.payload.category,
      );
    },
    backStep: (state) => {
      state.step = state.step - 1;
    },
    setPreviewFile: (state, action: { payload: { previewFile: string } }) => {
      state.previewFile = action.payload.previewFile;
    },
    setIdType: (
      state,
      action: {
        payload: { id_type: IDTypes };
      },
    ) => {
      state.onboarding.id_type = action.payload.id_type;
    },
    setKYC: (
      state,
      action: {
        payload: IOnboardingDraft;
      },
    ) => {
      Object.assign(state.onboarding, action.payload);
    },
    removeFile: (
      state,
      action: {
        payload: {
          filename: string;
          category: string;
        };
      },
    ) => {
      const { category, filename } = action.payload;

      try {
        //@ts-ignore
        state.onboarding[category] = state.onboarding[category].filter(
          //@ts-ignore
          (_, index) =>
            index !==
            //@ts-ignore
            state.onboarding[category].findIndex(
              (f: any) => f.filename === filename,
            ),
        );
      } catch (e) {
        console.error(e);
      }
    },
    addDocument: (
      state,
      action: {
        payload: {
          filename: string;
          category: string;
        };
      },
    ) => {
      const {
        payload: { filename, category },
      } = action;

      const newFile: FileStructure = {
        filename: filename,
        category: category,
      };

      //@ts-ignore
      if (state.onboarding[category]?.length) {
        //@ts-ignore
        state.onboarding[category].push(newFile);
      } else {
        //@ts-ignore
        state.onboarding[category] = [newFile];
      }
    },
    setSelectedCCY: (
      state,
      action: {
        payload: {
          selectedOrderId: string;
          selectedDestinationId?: string;
          selectedDestinationIndex?: number;
        };
      },
    ) => {
      Object.assign(state, action.payload);
    },
    clearSelectedCCY: (state) => {
      state.selectedDestinationId = "";
      state.selectedOrderId = "";
      state.selectedDestinationIndex = -1;
      state.isClaimTransferActionEnabled = false;
    },
    clearCcyConversionOrdersQuery: (state) => {
      state.ccyConversionOrdersQuery = {
        ...initialState.ccyConversionOrdersQuery,
      };
    },
    setCcyConversionOrdersQuery: (
      state,
      action: { payload: ICcyConversionOrdersQuery },
    ) => {
      // Only modifies the parameters of the state included in the action payload,
      // and maintains the rest
      Object.assign(state.ccyConversionOrdersQuery, action.payload);
    },
    setClaimTransferActionEnabled: (state) => {
      state.isClaimTransferActionEnabled = true;
    },
    setKYBQuery: (state, action: { payload: KYBQuery }) => {
      // Only modifies the parameters of the state included in the action payload,
      // and maintains the rest
      Object.assign(state.kybQuery, action.payload);
    },
  },
});

export const {
  reset,
  setKYBQuery,
  resetSubaccount,
  setSubaccount,
  setKYC,
  setInternalUserEdited,
  setSelectedCCY,
  addDocument,
  clearCcyConversionOrdersQuery,
  removeFile,
  clearSelectedCCY,
  toggleShowTimes,
  setCcyConversionOrdersQuery,
  setClaimTransferActionEnabled,
  setIdType,
  forwardStep,
  backStep,
  setPreviewFile,
  setUploading,
  removeUploading,
  setUserEdited,
  removeUserEdited,
  removeInternalUserEdited,
  setGeoLocation,
  resetLocation,
} = globalSlice.actions;

export type GlobalState = ReturnType<typeof globalSlice.reducer>;
